import EventDispatcher from './util/EventDispatcher';
import CookieBanner from './vue/components/CookieBanner.vue';

/**
 * This cookies modules export a `cookies` object to the window object.
 *
 * Retrieve consent status:
 * window.cookies.hasConsent(window.cookies.CONSENT_MARKETING)
 *
 * Subscribe to consent changes (by the visitor accepting cookies):
 * window.cookies.addEventListener('consent', function({ detailed: { levels }}) {
 *   // the `levels` argument contains a list of consent that has been given
 *   if (levels.includes(window.cookies.CONSENT_SOCIALMEDIA)) {
 *     // Consent for social media cookies has been given
 *   }
 * })
 */

const CONSENT_FUNCTIONAL = 1;
const CONSENT_ANALYTICS = 2;
const CONSENT_MARKETING = 3;
const CONSENT_SOCIALMEDIA = 4;

const CONSENT_LEVEL_NAMES = {
  [CONSENT_FUNCTIONAL]: 'functional',
  [CONSENT_ANALYTICS]: 'analytics',
  [CONSENT_MARKETING]: 'marketing',
  [CONSENT_SOCIALMEDIA]: 'socialmedia',
};

const DEFAULT_CONSENT_LEVELS = [
  CONSENT_FUNCTIONAL,
  CONSENT_ANALYTICS,
  CONSENT_MARKETING,
  CONSENT_SOCIALMEDIA,
];

const eventDispatcher = new EventDispatcher();

const addEventListener = (type, listener) =>
  eventDispatcher.addEventListener(type, listener);

let bannerVueApp;

// Change the cookie version if the cookie policy changes
// This will prompt new permission request to visitors.
const cookieVersion = 'v20220321';
const consentLevels = getConsentCookie(cookieVersion);

// Detect if we can actually set cookies
const cookiesDisabled = getCookiesDisabled();

if (!consentLevels || !consentLevels.includes(CONSENT_SOCIALMEDIA)) {
  showSocialMediaConsentOverlays();
}
eventDispatcher.addEventListener('consent', function ({ detail: { levels } }) {
  if (levels.includes(CONSENT_SOCIALMEDIA)) {
    hideSocialMediaConsentOverlays();
  }
});

/**
 * Checks if cookies are disabled, returns true cookies can't be set
 * @returns {Boolean} If cookies are disabled
 */
function getCookiesDisabled() {
  const name = 'cookietest';
  if (!consentLevels) {
    setCookie(name, 'abc', 7);
    if (!getCookie(name)) {
      return true;
    }
    removeCookie(name);
  }
  return false;
}

/**
 * Set consent cookie
 * @param {Array} consentLevels: array of consent levels (see top of file for
 * consent level definitions)
 */
function setConsentCookie(consentLevels) {
  setCookie('cookie-accept-' + cookieVersion, consentLevels.join(','), 365);
  eventDispatcher.dispatchEvent(
    new CustomEvent('consent', { detail: { levels: consentLevels } })
  );
}

function getConsentCookie(version) {
  const cookie = getCookie('cookie-accept-' + version);
  if (!cookie) return null;
  const levels = cookie.split(',').map(s => parseInt(s));
  if (levels.length === 0) {
    console.error("Found consent cookie, but couldn't parse levels: " + cookie);
    return null;
  }
  if (levels.filter(c => Number.isNaN(c)).length > 0) {
    console.error('Failed to parse consent cookie: ' + cookie);
    return null;
  }
  return levels;
}

function getCookie(name) {
  var value = '; ' + document.cookie;
  var parts = value.split('; ' + name + '=');
  if (parts.length == 2) return parts.pop().split(';').shift();
  return undefined;
}

function setCookie(cname, cvalue, exdays) {
  if (!cname) {
    console.error('setCookie: no cname defined');
  }
  if (!cvalue) {
    console.error('setCookie: no cvalue defined');
  }
  if (!exdays) {
    console.error('setCookie: no exdays defined');
  }
  var d = new Date();
  d.setTime(d.getTime() + exdays * 24 * 60 * 60 * 1000);
  var expires = 'expires=' + d.toUTCString();
  document.cookie =
    cname + '=' + cvalue + '; ' + expires + ';path=/;samesite=lax;';
}

function removeCookie(cname) {
  document.cookie = cname + '=; Max-Age=-99999999;';
}

// Delete all cookies when visitor consent is revoked
function deleteAllCookies() {
  var cookies = document.cookie.split(';');

  for (var i = 0; i < cookies.length; i++) {
    var cookie = cookies[i];
    var eqPos = cookie.indexOf('=');
    var name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
    document.cookie = name + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;';
  }

  location.reload();
}

// Show cookiebar when cookies haven't been accepted yet
function showCookiebanner() {
  // Don't try to show cookie banner when cookies are disabled anyway
  if (cookiesDisabled) {
    return false;
  }
  // Don't show cookie banner when visitor already gave consent
  if (consentLevels && consentLevels.length > 0) {
    return false;
  }

  openBanner({ banner: true, modal: false });

  return true;
}

function showCookieSettings() {
  openBanner({ banner: false, modal: true });
}

function openBanner({ banner, modal }) {
  // If the app already was created, just call the right methods to
  // open either the modal or the banner
  if (bannerVueApp) {
    if (banner) {
      bannerVueApp.$refs.banner.showBanner();
    } else if (modal) {
      bannerVueApp.$refs.banner.openPreferences();
    }
    return;
  }

  bannerVueApp = new Vue({
    el: '#cookie-banner-app',
    components: {
      CookieBanner: CookieBanner,
    },
    render: h =>
      h(CookieBanner, {
        ref: 'banner',
        props: {
          banner,
          modal,
          consentLevels: [
            {
              id: CONSENT_FUNCTIONAL,
              name: 'functional',
              defaultValue: true,
              disabled: true,
            },
            { id: CONSENT_ANALYTICS, name: 'analytics', defaultValue: false },
            { id: CONSENT_MARKETING, name: 'marketing', defaultValue: false },
            {
              id: CONSENT_SOCIALMEDIA,
              name: 'socialmedia',
              defaultValue: false,
            },
          ],
        },
        on: {
          consent: (defaultLevels, customLevels) => {
            const levels = defaultLevels
              ? DEFAULT_CONSENT_LEVELS
              : customLevels;
            // Store cookie
            setConsentCookie(levels);
            // Track
            levels.forEach(function (level) {
              window.tracking.trackEvent(
                'CookieBanner',
                'Accept',
                CONSENT_LEVEL_NAMES[level]
              );
            });
          },
        },
      }),
  });
}

function hasConsent(level) {
  if (!consentLevels) return false;
  if (level) return consentLevels.indexOf(level) !== -1;
  return consentLevels && consentLevels.length > 0;
}

function showSocialMediaConsentOverlays() {
  const overlays = [...document.getElementsByClassName('socialmedia-consent')];
  overlays.forEach(overlay => {
    overlay.classList.add('show');
  });
}

function hideSocialMediaConsentOverlays() {
  const overlays = [...document.getElementsByClassName('socialmedia-consent')];
  overlays.forEach(overlay => {
    overlay.classList.remove('show');
  });
}

export const cookies = {
  CONSENT_FUNCTIONAL,
  CONSENT_ANALYTICS,
  CONSENT_MARKETING,
  CONSENT_SOCIALMEDIA,

  getCookie,
  setCookie,
  deleteAllCookies,
  hasConsent,
  showCookiebanner,
  showCookieSettings,

  addEventListener,
};
